import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { Sparkle, TrendDown } from "@phosphor-icons/react";
import { Brain, Heart, Ribbon, Droplet } from "lucide-react";
import { toast } from "sonner";
import SpecialtyTwin from "@/components/SpecialtyTwin";
import { useTelemetry } from "@/lib/telemetry";

function SliderRow({ label, value, setValue, min = 0, max = 100, step = 1, unit = "%", testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="ros-label">{label}</div>
        <div className="font-mono text-sm text-white">{value}{unit}</div>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => setValue(v[0])}
        min={min}
        max={max}
        step={step}
        className="mt-3"
      />
    </div>
  );
}

const HUB_META = {
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
};
const TAB_ORDER = ["nephrology", "cardiology", "neurology", "oncology"];

export default function DigitalTwin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return TAB_ORDER.includes(t) ? t : "nephrology";
  });
  const setActive = (t) => { setTab(t); setSearchParams({ tab: t }); };
  const meta = HUB_META[tab];
  useTelemetry("digital-twin", "tab_switch", tab);

  return (
    <div className="space-y-6" data-testid="digital-twin-hub">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}>
            <Brain size={22} style={{ color: meta.accent }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Digital Twin</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              Forward-looking projections per specialty. Nephrology uses an interactive scenario builder;
              other departments compute deterministic trajectories from your logged facts.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="twin-tabs">
        {TAB_ORDER.map((s) => {
          const m = HUB_META[s]; const Icon = m.icon; const active = tab === s;
          return (
            <button key={s} onClick={() => setActive(s)}
                    data-testid={`twin-tab-${s}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      active ? "text-black border-transparent"
                             : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white"
                    }`}
                    style={active ? { background: m.accent } : {}}>
              <Icon size={14} /> {m.label}
            </button>
          );
        })}
      </div>

      {tab === "nephrology" ? <NephrologyTwin /> : <SpecialtyTwin specialty={tab} />}
    </div>
  );
}

function NephrologyTwin() {
  const [profile, setProfile] = useState(null);
  const [baselineEgfr, setBaselineEgfr] = useState(52);
  const [bp, setBp] = useState(140);
  const [dietAdh, setDietAdh] = useState(50);
  const [dmCtrl, setDmCtrl] = useState(60);
  const [protein, setProtein] = useState(50);
  const [projection, setProjection] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [loadingNarr, setLoadingNarr] = useState(false);

  useEffect(() => {
    (async () => {
      const [profRes, egfrRes] = await Promise.all([
        api.get("/patient/profile"),
        api.get("/tracker/egfr"),
      ]);
      setProfile(profRes.data);
      if (egfrRes.data.length) {
        setBaselineEgfr(egfrRes.data[egfrRes.data.length - 1].egfr);
      }
    })();
  }, []);

  const runProjection = async (withNarrative = false) => {
    if (!profile) return;
    setLoadingNarr(withNarrative);
    try {
      const res = await api.post("/twin/project", {
        baseline_egfr: baselineEgfr,
        age: profile.age,
        sex: profile.sex,
        bp_systolic: bp,
        diet_adherence: dietAdh,
        diabetes_control: dmCtrl,
        protein_intake: protein,
      });
      setProjection(res.data);
      if (res.data.narrative) setNarrative(res.data.narrative);
    } catch (e) {
      toast.error("Projection failed");
    } finally {
      setLoadingNarr(false);
    }
  };

  useEffect(() => {
    if (profile) runProjection(false);
  }, [profile, baselineEgfr, bp, dietAdh, dmCtrl, protein]);

  const finalEgfr = projection?.series?.[projection.series.length - 1]?.egfr;
  const dialysisYear = projection?.dialysis_year;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="twin-grid">
      {/* Sliders */}
      <div className="ros-card lg:col-span-2" data-testid="twin-controls">
        <div className="ros-label">Modifiable factors</div>
        <h2 className="font-heading text-2xl mt-1">Your digital twin</h2>
        <p className="text-xs text-[#A3ACA7] mt-1">
          Adjust factors below. The projection updates in real time.
        </p>

        <div className="mt-6 space-y-6">
          <SliderRow label="Blood pressure (systolic)" value={bp} setValue={setBp}
                     min={100} max={180} step={1} unit=" mmHg" testId="slider-bp" />
          <SliderRow label="Diet adherence" value={dietAdh} setValue={setDietAdh} testId="slider-diet" />
          <SliderRow label="Diabetes control" value={dmCtrl} setValue={setDmCtrl} testId="slider-dm" />
          <SliderRow label="Protein restriction" value={protein} setValue={setProtein} testId="slider-protein" />
        </div>

        <Button
          onClick={() => runProjection(true)}
          disabled={loadingNarr}
          data-testid="twin-ai-btn"
          className="rounded-full mt-8 w-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold gap-2"
        >
          <Sparkle size={16} weight="fill" />
          {loadingNarr ? "Generating insight…" : "Get AI insight on this scenario"}
        </Button>
      </div>

      {/* Chart */}
      <div className="ros-card lg:col-span-3" data-testid="twin-chart-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label">10-year eGFR projection</div>
            <h2 className="font-heading text-2xl mt-1">Your trajectory</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 text-right">
            <div>
              <div className="ros-label" style={{ fontSize: 9 }}>Baseline</div>
              <div className="ros-metric text-xl mt-1">{baselineEgfr.toFixed(1)}</div>
            </div>
            <div>
              <div className="ros-label" style={{ fontSize: 9 }}>Year 10</div>
              <div className="ros-metric text-xl mt-1" data-testid="twin-year10">
                {finalEgfr != null ? finalEgfr.toFixed(1) : "—"}
              </div>
            </div>
            <div>
              <div className="ros-label" style={{ fontSize: 9 }}>Dialysis?</div>
              <div className="ros-metric text-xl mt-1"
                   style={{ color: dialysisYear ? "#F87171" : "#4ADE80" }}
                   data-testid="twin-dialysis-year">
                {dialysisYear != null ? `Y${dialysisYear}` : "No"}
              </div>
            </div>
          </div>
        </div>

        {projection?.series && (
          <div className="mt-6 h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={projection.series} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" stroke="#A3ACA7" fontSize={11}
                       tickFormatter={(v) => `Y${v}`} tickLine={false} axisLine={false} />
                <YAxis stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false}
                       domain={[0, "auto"]} width={40} />
                <Tooltip
                  contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12 }}
                  formatter={(v) => [`${v} mL/min`, "eGFR"]}
                  labelFormatter={(l) => `Year ${l}`}
                />
                <ReferenceLine y={15} stroke="#F87171" strokeDasharray="6 6" label={{ value: "Dialysis threshold", fill: "#F87171", fontSize: 10, position: "insideBottomRight" }} />
                <ReferenceLine y={60} stroke="#FBBF24" strokeDasharray="6 6" label={{ value: "CKD stage 3", fill: "#FBBF24", fontSize: 10, position: "insideBottomRight" }} />
                <Line type="monotone" dataKey="egfr" stroke="#4ADE80" strokeWidth={3}
                      dot={{ r: 3, fill: "#4ADE80" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 text-xs text-[#A3ACA7]">
          <TrendDown size={14} weight="duotone" />
          Projected annual decline: <span className="text-white font-semibold">
            {projection?.annual_decline?.toFixed(2) || "—"} mL/min</span>
        </div>
      </div>

      {/* AI narrative */}
      {narrative && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="ros-card lg:col-span-5"
          data-testid="twin-narrative"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-2xl p-3 bg-[rgba(74,222,128,0.15)]">
              <Sparkle size={22} weight="duotone" color="#4ADE80" />
            </div>
            <div className="flex-1">
              <div className="ros-label">Renna · AI clinical narrative</div>
              <p className="mt-2 text-white/90 leading-relaxed" style={{ fontSize: 15 }}>
                {narrative}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
